import React, { useEffect, useMemo, useState } from 'react';
import {
  changeCognitoPassword,
  getCognitoUser,
  updateCognitoUserAttributes,
} from '../auth/cognito';
import { useAuth } from '../auth/AuthContext';

const LANGUAGE_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'pl', label: 'Polish' },
];

const PASSWORD_FIELDS = [
  { name: 'oldPassword', label: 'Old password', autoComplete: 'current-password' },
  { name: 'newPassword', label: 'New password', autoComplete: 'new-password' },
  { name: 'confirmPassword', label: 'Confirm new password', autoComplete: 'new-password' },
];

function attributesToMap(attributes = []) {
  return attributes.reduce((acc, item) => {
    if (item?.Name) acc[item.Name] = item.Value || '';
    return acc;
  }, {});
}

function isValidPhoneNumber(value) {
  return /^\+[1-9]\d{7,14}$/.test(value);
}

function formatRoles(groups = []) {
  if (!groups.length) return 'No role assigned';
  return groups.join(', ');
}

export default function SettingsPage() {
  const { tokens, user, logout, refreshSession } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    givenName: '',
    familyName: '',
    phoneNumber: '',
    locale: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [passwordResetKey, setPasswordResetKey] = useState(0);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');

  const roles = user?.adminRoles || [];
  const roleError = user?.roleError || '';
  const canUpdateProfile = Boolean(user?.hasSingleAdminRole);
  const username = profile?.Username || user?.username || user?.email || '';

  const profileValues = useMemo(() => {
    return attributesToMap(profile?.UserAttributes);
  }, [profile]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getCognitoUser(tokens?.accessToken);
      const values = attributesToMap(data.UserAttributes);
      setProfile(data);
      setForm({
        givenName: values.given_name || '',
        familyName: values.family_name || '',
        phoneNumber: values.phone_number || '',
        locale: values.locale || '',
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens?.accessToken]);

  const updateForm = (field) => (event) => {
    const value = event.currentTarget.value;
    setNotice('');
    setError('');
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updatePasswordForm = (field) => (event) => {
    const value = event.currentTarget.value;
    // Keep feedback in place until submission: removing it while Chrome dismisses
    // its password-generation popup shifts the form and can leave the page blank.
    setPasswordForm((current) => ({ ...current, [field]: value }));
  };

  const refreshIdentity = async () => {
    try {
      await refreshSession();
    } catch {
      // Profile attributes were already updated; token refresh can wait for the next login.
    }
  };

  const saveName = async (event) => {
    event.preventDefault();
    if (!canUpdateProfile) {
      setError(roleError);
      return;
    }
    const givenName = form.givenName.trim();
    const familyName = form.familyName.trim();
    if (!givenName || !familyName) {
      setError('First name and last name are required.');
      return;
    }

    try {
      setSaving('name');
      setError('');
      setNotice('');
      await updateCognitoUserAttributes(tokens?.accessToken, {
        given_name: givenName,
        family_name: familyName,
      });
      await loadProfile();
      await refreshIdentity();
      setNotice('Name updated.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving('');
    }
  };

  const savePhone = async (event) => {
    event.preventDefault();
    if (!canUpdateProfile) {
      setError(roleError);
      return;
    }
    const phoneNumber = form.phoneNumber.trim();
    if (!phoneNumber) {
      setError('Phone number is required.');
      return;
    }
    if (!isValidPhoneNumber(phoneNumber)) {
      setError('Phone number must use E.164 format, for example +15551234567.');
      return;
    }

    try {
      setSaving('phone');
      setError('');
      setNotice('');
      await updateCognitoUserAttributes(tokens?.accessToken, {
        phone_number: phoneNumber,
      });
      await loadProfile();
      await refreshIdentity();
      setNotice('Phone number updated.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving('');
    }
  };

  const saveLanguage = async (event) => {
    event.preventDefault();
    if (!canUpdateProfile) {
      setError(roleError);
      return;
    }
    if (!form.locale) {
      setError('Choose a language.');
      return;
    }

    try {
      setSaving('language');
      setError('');
      setNotice('');
      await updateCognitoUserAttributes(tokens?.accessToken, {
        locale: form.locale,
      });
      await loadProfile();
      await refreshIdentity();
      setNotice('Language updated.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving('');
    }
  };

  const handlePasswordError = (message) => {
    setPasswordForm((current) => ({ ...current, newPassword: '', confirmPassword: '' }));
    setVisiblePasswords((current) => ({ ...current, newPassword: false, confirmPassword: false }));
    setPasswordResetKey((current) => current + 1);
    setNotice('');
    setError(message);
  };

  const changePassword = async (event) => {
    event.preventDefault();
    if (!canUpdateProfile) {
      setError(roleError);
      return;
    }
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      handlePasswordError('Old password, new password, and confirm password are required.');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      handlePasswordError('New password and confirm password must match.');
      return;
    }
    if (passwordForm.newPassword.length < 12) {
      handlePasswordError('New password must be at least 12 characters.');
      return;
    }

    try {
      setSaving('password');
      setError('');
      setNotice('');
      await changeCognitoPassword(tokens?.accessToken, passwordForm.oldPassword, passwordForm.newPassword);
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setVisiblePasswords({});
      setPasswordResetKey((current) => current + 1);
      setNotice('Password changed.');
      logout();
    } catch (e) {
      handlePasswordError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving('');
    }
  };

  return (
    <div className="page profile-page">
      <div className="profile-header">
        <div>
          <h2>My Profile</h2>
          <p className="muted">Manage your WebAdmin profile details and password.</p>
        </div>
        <button className="btn btn-secondary" type="button" onClick={logout}>
          Log out
        </button>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}

      <section className="profile-summary panel">
        <div>
          <div className="profile-label">Username</div>
          <div className="profile-value">{username || 'Not available'}</div>
        </div>
        <div>
          <div className="profile-label">Email</div>
          <div className="profile-value">{profileValues.email || user?.email || 'Not available'}</div>
        </div>
        <div>
          <div className="profile-label">Role</div>
          <div className="profile-value">{user?.activeRole || formatRoles(roles)}</div>
        </div>
      </section>

      {!canUpdateProfile ? (
        <div className="alert alert-warning">
          {roleError} Ask an OWNER to update this user so exactly one role is assigned.
        </div>
      ) : null}

      {loading ? (
        <div className="panel">Loading profile...</div>
      ) : (
        <div className="profile-sections">
          <form className="panel profile-form" onSubmit={saveName}>
            <h3>Name</h3>
            <div className="form-grid">
              <label>
                <span>First name</span>
                <input value={form.givenName} onChange={updateForm('givenName')} autoComplete="given-name" />
              </label>
              <label>
                <span>Last name</span>
                <input value={form.familyName} onChange={updateForm('familyName')} autoComplete="family-name" />
              </label>
            </div>
            <button className="btn btn-primary" type="submit" disabled={!canUpdateProfile || saving === 'name'}>
              {saving === 'name' ? 'Saving...' : 'Confirm and Change Name'}
            </button>
          </form>

          <form className="panel profile-form" onSubmit={savePhone}>
            <h3>Phone Number</h3>
            <label>
              <span>Phone number</span>
              <input
                value={form.phoneNumber}
                onChange={updateForm('phoneNumber')}
                placeholder="+15551234567"
                autoComplete="tel"
              />
            </label>
            <button className="btn btn-primary" type="submit" disabled={!canUpdateProfile || saving === 'phone'}>
              {saving === 'phone' ? 'Saving...' : 'Update Phone Number'}
            </button>
          </form>

          <form className="panel profile-form" onSubmit={saveLanguage}>
            <h3>Language</h3>
            <label>
              <span>Choose language</span>
              <select value={form.locale} onChange={updateForm('locale')}>
                {LANGUAGE_OPTIONS.map((option) => (
                  <option key={option.value || 'not-set'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <button className="btn btn-primary" type="submit" disabled={!canUpdateProfile || saving === 'language'}>
              {saving === 'language' ? 'Saving...' : 'Change Language'}
            </button>
          </form>

          <form className="panel profile-form" onSubmit={changePassword}>
            <h3>Password</h3>
            {PASSWORD_FIELDS.map(({ name, label, autoComplete }) => (
              <div className="password-field" key={name}>
                <label htmlFor={name}>{label}</label>
                <div className="password-input">
                  <input
                    // Replace rejected new-password inputs to clear browser-managed autofill state, too.
                    key={name === 'oldPassword' ? name : `${name}-${passwordResetKey}`}
                    id={name}
                    name={name}
                    value={passwordForm[name]}
                    onChange={updatePasswordForm(name)}
                    type={visiblePasswords[name] ? 'text' : 'password'}
                    autoComplete={autoComplete}
                    aria-describedby={name === 'newPassword' ? 'password-requirements' : undefined}
                  />
                  <button
                    className="password-toggle"
                    type="button"
                    aria-label={`${visiblePasswords[name] ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
                    aria-controls={name}
                    onClick={() => setVisiblePasswords((current) => ({ ...current, [name]: !current[name] }))}
                  >
                    {visiblePasswords[name] ? 'Hide' : 'Show'}
                  </button>
                </div>
                {name === 'newPassword' ? (
                  <div className="password-requirements" id="password-requirements">
                    <p>New password must contain:</p>
                    <ul>
                      <li>At least 12 characters</li>
                      <li>At least one uppercase letter (A–Z)</li>
                      <li>At least one lowercase letter (a–z)</li>
                      <li>At least one number (0–9)</li>
                      <li>At least one special character (for example, ! @ # $)</li>
                    </ul>
                  </div>
                ) : null}
              </div>
            ))}
            <button className="btn btn-primary" type="submit" disabled={!canUpdateProfile || saving === 'password'}>
              {saving === 'password' ? 'Saving...' : 'Confirm and Change Password'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
